import { Pipe, PipeTransform } from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { marked } from "marked";
import markedKatex from "marked-katex-extension";
import DOMPurify from "dompurify";

// Render LaTeX math ($inline$ and $$block$$) via KaTeX. Registered once on the
// shared `marked` singleton, so every markdown render picks it up.
// `throwOnError: false` renders malformed math as red text instead of crashing.
marked.use(markedKatex({ throwOnError: false }));

/**
 * Converts a Markdown string (with LaTeX math) to sanitized HTML.
 *
 * We sanitize with DOMPurify — configured to keep KaTeX's markup (spans, MathML,
 * and the inline `style` attributes KaTeX needs for fractions/spacing) — and
 * return SafeHtml. This is why we don't rely on Angular's default `[innerHTML]`
 * sanitizer, which strips those inline styles and would break math layout.
 */
@Pipe({
  name: "markdown",
  standalone: true,
})
export class MarkdownPipe implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) {}

  transform(value: string | null | undefined): SafeHtml {
    if (!value) return "";
    const html = marked.parse(value, { async: false }) as string;
    const clean = DOMPurify.sanitize(html, {
      // KaTeX emits MathML alongside its HTML; allow it through.
      USE_PROFILES: { html: true, mathMl: true, svg: true },
    });
    return this.sanitizer.bypassSecurityTrustHtml(clean);
  }
}
